import { ID_SEPARATOR } from 'constants/appConstants';
import {
  CopyDataCategory,
  CopyMatchRule,
  ICopyDataFilter,
} from 'interfaces/ICopyPaddockData';
import { IInput } from 'interfaces/IInput';
import { IInputStep, InputStepType } from 'interfaces/IInputStep';
import { IInputTask, InputTaskCategory } from 'interfaces/IInputTask';
import { getTaskSteps } from 'pages/FarmsPage/Workspace/Tasks/stepCalculations';

/** Every real step type; Reserved is never saved on a step. */
export type CopyableStepType = Exclude<InputStepType, InputStepType.Reserved>;

/** The copy category of each step type; a Record so a new step type fails the type-check. */
export const COPY_CATEGORY_BY_STEP_TYPE: Record<
  CopyableStepType,
  CopyDataCategory
> = {
  [InputStepType.Generic]: CopyDataCategory.Operation,
  [InputStepType.Application]: CopyDataCategory.Operation,
  [InputStepType.CropProtection]: CopyDataCategory.PlantProtection,
  [InputStepType.Adjuvant]: CopyDataCategory.PlantProtection,
  [InputStepType.SoilInput]: CopyDataCategory.SoilInput,
  [InputStepType.Biostimulant]: CopyDataCategory.Biostimulant,
  [InputStepType.Production]: CopyDataCategory.Production,
  [InputStepType.Seed]: CopyDataCategory.Seed,
  [InputStepType.SeedDress]: CopyDataCategory.SeedDressing,
};

/** Display order, which is also the order categories are sent in. */
export const COPY_DATA_CATEGORY_ORDER: CopyDataCategory[] = [
  CopyDataCategory.Seed,
  CopyDataCategory.SeedDressing,
  CopyDataCategory.SoilInput,
  CopyDataCategory.PlantProtection,
  CopyDataCategory.Operation,
  CopyDataCategory.Biostimulant,
  CopyDataCategory.Production,
];

/** The step's copy category, or null for a step type with none (e.g. Reserved). */
export const getStepCopyCategory = (
  step: IInputStep
): CopyDataCategory | null =>
  COPY_CATEGORY_BY_STEP_TYPE[step.stepType as CopyableStepType] ?? null;

/** The source tasks a copy draws from: those in the Source mode, in order. */
export const getCopySourceTasks = (
  input: IInput,
  mode: InputTaskCategory
): IInputTask[] => input.tasks.filter((task) => task.category === mode);

/** Whether the document has a crop or variety to copy. */
export const hasCopyCropAndVariety = (input: IInput): boolean =>
  !!input.crop?.cropName || !!input.crop?.variety?.varietyName;

/** Task counts per category present in the tasks, in display order. */
export const countTasksByCopyCategory = (
  tasks: IInputTask[]
): ReadonlyMap<CopyDataCategory, number> => {
  const counts = new Map<CopyDataCategory, number>();
  tasks.forEach((task) => {
    const categories = new Set(
      getTaskSteps(task)
        .map(getStepCopyCategory)
        .filter((c): c is CopyDataCategory => c !== null)
    );
    categories.forEach((category) => {
      counts.set(category, (counts.get(category) ?? 0) + 1);
    });
  });
  return new Map(
    COPY_DATA_CATEGORY_ORDER.filter((c) => counts.has(c)).map((c) => [
      c,
      counts.get(c) ?? 0,
    ])
  );
};

const isStepInCategories = (
  step: IInputStep,
  categories: ReadonlySet<CopyDataCategory>
) => {
  const category = getStepCopyCategory(step);
  return category !== null && categories.has(category);
};

const hasStepInCategories = (
  task: IInputTask,
  categories: ReadonlySet<CopyDataCategory>
) => getTaskSteps(task).some((step) => isStepInCategories(step, categories));

/** The task's steps in the categories, which is what Exact match copies. */
export const getStepsInCategories = (
  task: IInputTask,
  categories: ReadonlySet<CopyDataCategory>
): IInputStep[] =>
  getTaskSteps(task).filter((step) => isStepInCategories(step, categories));

/** The data filter's choices for one source document and mode. */
export interface CopyDataFilterState {
  /** The source document and mode these choices belong to. */
  sourceKey: string;
  matchRule: CopyMatchRule;
  categories: CopyDataCategory[];
  includeCropAndVariety: boolean;
  /** Ticked tasks; the final selection. */
  taskIds: string[];
  /** Task ticks changed by hand since the categories or rule last set them. */
  hasManualTaskChanges: boolean;
}

/** What the filter is applied to: the source's tasks for its mode. */
export interface CopyDataFilterContext {
  tasks: IInputTask[];
  hasCropAndVariety: boolean;
  /** countTasksByCopyCategory(tasks), counted once per Source. */
  categoryCounts: ReadonlyMap<CopyDataCategory, number>;
  /** getLockedTaskIds(tasks), found once per Source. */
  lockedTaskIds: ReadonlySet<string>;
}

export const getCopyFilterSourceKey = (
  inputId: string,
  mode: InputTaskCategory
): string => `${inputId}${ID_SEPARATOR}${mode}`;

/** Everything ticked, which is where every source starts. */
export const createFullCopyFilter = (
  sourceKey: string,
  { tasks, categoryCounts }: CopyDataFilterContext,
  matchRule = CopyMatchRule.Contains
): CopyDataFilterState => ({
  sourceKey,
  matchRule,
  categories: Array.from(categoryCounts.keys()),
  includeCropAndVariety: true,
  taskIds: tasks.map((task) => task.taskId),
  hasManualTaskChanges: false,
});

const hasAllCategories = (
  categories: CopyDataCategory[],
  includeCropAndVariety: boolean,
  { hasCropAndVariety, categoryCounts }: CopyDataFilterContext
) => {
  const ticked = new Set(categories);
  return (
    (!hasCropAndVariety || includeCropAndVariety) &&
    Array.from(categoryCounts.keys()).every((c) => ticked.has(c))
  );
};

const hasCategorisedStep = (task: IInputTask) =>
  getTaskSteps(task).some((step) => getStepCopyCategory(step) !== null);

/**
 * Tasks with no categorised step: only copied in a full copy, so their tick
 * is fixed on, as unticking one could only leave it out.
 */
export const getLockedTaskIds = (tasks: IInputTask[]): ReadonlySet<string> =>
  new Set(
    tasks.filter((task) => !hasCategorisedStep(task)).map((task) => task.taskId)
  );

/**
 * Saved ticks fitted to the Source's current tasks: locked tasks always
 * ticked (also one a refetch adds), and tasks no longer there dropped.
 */
export const withLockedTaskTicks = (
  filter: CopyDataFilterState,
  { tasks, lockedTaskIds }: CopyDataFilterContext
): CopyDataFilterState => {
  const ticked = new Set(filter.taskIds);
  return {
    ...filter,
    taskIds: tasks
      .filter(
        (task) => ticked.has(task.taskId) || lockedTaskIds.has(task.taskId)
      )
      .map((task) => task.taskId),
  };
};

/** True when every category, Crop & Variety and every task is ticked. */
export const isFullCopyFilter = (
  filter: CopyDataFilterState,
  context: CopyDataFilterContext
): boolean => {
  const ticked = new Set(filter.taskIds);
  return (
    hasAllCategories(
      filter.categories,
      filter.includeCropAndVariety,
      context
    ) && context.tasks.every((task) => ticked.has(task.taskId))
  );
};

/**
 * The task ticks the categories give: the tasks with a step in a ticked
 * category. Tasks with no categorised step stay ticked, so ticking everything
 * again gives a full copy; a partial copy leaves them out anyway. Both rules
 * tick the same tasks; Exact match only trims their steps.
 */
export const getCategoryTaskIds = (
  categories: CopyDataCategory[],
  { tasks, lockedTaskIds }: CopyDataFilterContext
): string[] => {
  const ticked = new Set(categories);
  return tasks
    .filter(
      (task) =>
        lockedTaskIds.has(task.taskId) || hasStepInCategories(task, ticked)
    )
    .map((task) => task.taskId);
};

/**
 * New category ticks, bulk-updating only the tasks with a step in a changed
 * category; other tasks keep their ticks, including manual ones.
 */
export const applyCategoryChange = (
  filter: CopyDataFilterState,
  categories: CopyDataCategory[],
  includeCropAndVariety: boolean,
  { tasks }: CopyDataFilterContext
): CopyDataFilterState => {
  const before = new Set(filter.categories);
  const after = new Set(categories);
  const changed = new Set(
    COPY_DATA_CATEGORY_ORDER.filter((c) => before.has(c) !== after.has(c))
  );
  const ticked = new Set(filter.taskIds);
  tasks.forEach((task) => {
    if (!hasStepInCategories(task, changed)) return;
    if (hasStepInCategories(task, after)) ticked.add(task.taskId);
    else ticked.delete(task.taskId);
  });
  return {
    ...filter,
    categories: COPY_DATA_CATEGORY_ORDER.filter((c) => after.has(c)),
    includeCropAndVariety,
    taskIds: tasks
      .filter((task) => ticked.has(task.taskId))
      .map((task) => task.taskId),
  };
};

/** A task ticked or unticked by hand. */
export const applyTaskCheck = (
  filter: CopyDataFilterState,
  taskId: string,
  checked: boolean,
  { tasks }: CopyDataFilterContext
): CopyDataFilterState => {
  const ticked = new Set(filter.taskIds);
  if (checked) ticked.add(taskId);
  else ticked.delete(taskId);
  return {
    ...filter,
    taskIds: tasks
      .filter((task) => ticked.has(task.taskId))
      .map((task) => task.taskId),
    hasManualTaskChanges: true,
  };
};

/** A new rule, with the task ticks re-evaluated from the categories. */
export const applyMatchRuleChange = (
  filter: CopyDataFilterState,
  matchRule: CopyMatchRule,
  context: CopyDataFilterContext
): CopyDataFilterState => ({
  ...filter,
  matchRule,
  taskIds: getCategoryTaskIds(filter.categories, context),
  hasManualTaskChanges: false,
});

/**
 * Whether a task can be copied under a partial filter. Tasks with no
 * categorised step go only in a full copy, and Exact match skips a task with
 * no matching step.
 */
export const isTaskCopyable = (
  task: IInputTask,
  matchRule: CopyMatchRule,
  categories: ReadonlySet<CopyDataCategory>,
  { lockedTaskIds }: CopyDataFilterContext
): boolean => {
  if (lockedTaskIds.has(task.taskId)) return false;
  return (
    matchRule === CopyMatchRule.Contains ||
    hasStepInCategories(task, categories)
  );
};

/** The filter as sent on the copy request. */
export const toCopyDataFilter = (
  filter: CopyDataFilterState,
  context: CopyDataFilterContext
): ICopyDataFilter => {
  const present = context.categoryCounts;
  const includeCropAndVariety =
    context.hasCropAndVariety && filter.includeCropAndVariety;

  if (isFullCopyFilter(filter, context)) {
    return {
      isFullCopy: true,
      matchRule: filter.matchRule,
      categories: Array.from(present.keys()),
      taskIds: context.tasks.map((task) => task.taskId),
      includeCropAndVariety,
    };
  }

  const categories = new Set(filter.categories);
  const tickedTasks = new Set(filter.taskIds);
  return {
    isFullCopy: false,
    matchRule: filter.matchRule,
    categories: Array.from(present.keys()).filter((c) => categories.has(c)),
    taskIds: context.tasks
      .filter(
        (task) =>
          tickedTasks.has(task.taskId) &&
          isTaskCopyable(task, filter.matchRule, categories, context)
      )
      .map((task) => task.taskId),
    includeCropAndVariety,
  };
};

/** One task as copied, with the steps it keeps. */
export interface CopiedTask {
  task: IInputTask;
  steps: IInputStep[];
}

/** The tasks and steps a request's filter copies from the source tasks. */
export const applyCopyDataFilter = (
  tasks: IInputTask[],
  filter: ICopyDataFilter
): CopiedTask[] => {
  if (filter.isFullCopy) {
    return tasks.map((task) => ({ task, steps: getTaskSteps(task) }));
  }
  const taskIds = new Set(filter.taskIds);
  const categories = new Set(filter.categories);
  return tasks
    .filter((task) => taskIds.has(task.taskId))
    .map((task) => ({
      task,
      steps:
        filter.matchRule === CopyMatchRule.Contains
          ? getTaskSteps(task)
          : getStepsInCategories(task, categories),
    }))
    .filter(({ steps }) => steps.length > 0);
};
